// Central site configuration & brand contact details.
//
// These are DEFAULTS. The live values are stored in Firestore under
// settings/site and edited in the admin panel under Settings, so the client
// can change the phone number, WhatsApp number and social links without a
// deploy. Components read them through useSite() (src/hooks/content.js),
// which falls back to this object while the settings load, and permanently
// if Firestore is ever unreachable — so the site never renders a blank phone
// number or a dead link.

export const SITE = {
  name: "SKY Training & Consultancy Pte Ltd",
  shortName: "SKY",
  uen: "202630588G",
  domain: "skyfiresafety.sg",
  url: "https://skyfiresafety.sg",
  mobile: "+65 9068 0058",
  mobileRaw: "6590680058",
  // Digits only, international format, no "+" or spaces — this is what wa.me
  // expects. The full link is derived, never stored, so editing the number in
  // one place updates every WhatsApp button on the site.
  whatsappNumber: "6590680058",
  whatsappMessage:
    "Hi SKY Training & Consultancy, I would like to enquire about your safety consultancy/training services.",
  email: "enquiries@skyfiresafety.sg",
  tagline: "Safer Workplaces. Better Prepared Organisations.",
  logo: "/sky-logo.png",
  location: "Singapore",
  // Empty means "not set" — the icon is simply not rendered.
  facebook: "",
  instagram: "",
  tiktok: "",
};

/** Strip anything that is not a digit, so "+65 9068 0058" becomes "6590680058". */
export const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

/**
 * Build a wa.me link from a settings object.
 *
 * Accepts the live settings so the number stays editable; falls back to SITE
 * when called without one.
 */
export function buildWhatsappLink(settings, message) {
  const s = settings || SITE;
  const number = digitsOnly(s.whatsappNumber || s.mobileRaw || SITE.whatsappNumber);
  const text = message || s.whatsappMessage || SITE.whatsappMessage;
  return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
}

/**
 * Kept for anything that still needs a link without access to live settings
 * (it uses the defaults above).
 */
export const whatsappLink = (message) => buildWhatsappLink(SITE, message);

// Site imagery.
//
// These were originally hot-linked from the Emergent preview CDN, which is
// tied to the generating job and would take every image on the site with it
// if that job were ever removed. They are now served from Firebase Hosting
// alongside the app: same CDN as the rest of the site, covered by the
// immutable cache headers in firebase.json, and resized to 1600px max and
// converted to WebP (8.2 MB -> 1.1 MB across the ten files).
export const IMAGES = {
  hero: "/images/hero.webp",
  wshInspection: "/images/wsh-inspection.webp",
  fireResponse: "/images/fire-response.webp",
  extinguisher: "/images/extinguisher.webp",
  consultantMeeting: "/images/consultant-meeting.webp",
  classroom: "/images/classroom.webp",
  evacuation: "/images/evacuation.webp",
  riskAssessment: "/images/risk-assessment.webp",
  corporateProgramme: "/images/corporate-programme.webp",
  industrial: "/images/industrial.webp",
};

export const NAV_LINKS = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "Services", path: "/services", hasDropdown: true },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Blog", path: "/blog" },
  { name: "Contact Us", path: "/contact" },
];

export const APPROACH = [
  {
    step: "01",
    title: "Assess",
    description:
      "Identify hazards, risks, compliance gaps and weaknesses across your workplace and operations.",
  },
  {
    step: "02",
    title: "Advise",
    description:
      "Provide practical recommendations aligned with applicable legislation, codes, standards and good practices.",
  },
  {
    step: "03",
    title: "Implement",
    description:
      "Help clients put appropriate controls, procedures, systems and training into practice.",
  },
  {
    step: "04",
    title: "Improve",
    description:
      "Review effectiveness and support continual improvement of safety performance over time.",
  },
];

export const WHY_CHOOSE = [
  {
    icon: "Wrench",
    title: "Practical Solutions",
    description: "Recommendations designed for real operational environments.",
  },
  {
    icon: "GraduationCap",
    title: "Professional Expertise",
    description:
      "Safety solutions based on professional assessment and industry knowledge.",
  },
  {
    icon: "ShieldCheck",
    title: "Compliance Focused",
    description:
      "Solutions aligned with Singapore legislation and applicable standards.",
  },
  {
    icon: "Factory",
    title: "Industry-Focused",
    description:
      "Approaches adapted to different industries, workplaces and risk profiles.",
  },
  {
    icon: "Headset",
    title: "Responsive Support",
    description: "Clear communication and practical client support.",
  },
  {
    icon: "TrendingUp",
    title: "Continuous Improvement",
    description:
      "Helping organisations improve safety performance beyond basic compliance.",
  },
];

export const INDUSTRIES = [
  { name: "Manufacturing", icon: "Factory" },
  { name: "Construction", icon: "HardHat" },
  { name: "Commercial Buildings", icon: "Building2" },
  { name: "Logistics & Warehousing", icon: "Truck" },
  { name: "Offices", icon: "Briefcase" },
  { name: "Industrial Facilities", icon: "Cog" },
];

export const CORE_AREAS = [
  {
    id: "wsh",
    title: "Workplace Safety & Health",
    image: IMAGES.wshInspection,
    icon: "ShieldCheck",
    items: [
      "Risk Assessment",
      "Workplace Inspections",
      "WSH Audits",
      "Safety Management Systems",
      "Incident Investigation",
      "WSH Personnel Support",
      "Compliance Advisory",
    ],
    link: "/services/wsh-consultancy",
  },
  {
    id: "fire",
    title: "Fire Safety & Emergency Preparedness",
    image: IMAGES.fireResponse,
    icon: "Flame",
    items: [
      "Fire Safety Consultancy",
      "Emergency Response Planning",
      "Fire Safety Inspections",
      "Evacuation Exercises",
      "Table-Top Exercises",
      "Emergency Preparedness",
      "Fire Safety Training",
    ],
    link: "/services/fire-safety-consultancy",
  },
];

export const MARQUEE_ITEMS = [
  "Workplace Safety & Health Act",
  "Risk Assessment & Risk Management",
  "Fire Safety Consultancy",
  "Emergency Response Planning",
  "Safety Management Systems",
  "Evacuation & Table-Top Exercises",
  "WSH Training",
  "Fire Warden Training",
  "Continual Improvement",
];
